import { Modal as MuiModal, ModalProps as MuiModalProps } from '@mui/material';
import { styled as muiStyled, css } from '@mui/material/styles';
import React from 'react';

import Container from './container';

// Relax the single children restriction from the default MuiModal
type CustomMuiModalProps = Omit<MuiModalProps, 'children' | 'title'>;

interface Props extends React.PropsWithChildren<CustomMuiModalProps> {
  className?: string;
  /**
   * Title to be display on top of the modal
   */
  title?: string;
  /**
   * When true, hides the close (X) button. Use when the modal must not be
   * dismissed by the user (e.g. enforced 2FA setup).
   */
  hideCloseButton?: boolean;
}

const ModalImpl = React.forwardRef<HTMLDivElement, Props>(
  ({ className, title, children, onClose, hideCloseButton, ...props }, ref) => {
    const customOnClose: MuiModalProps['onClose'] = (evt, reason) => {
      if (reason === 'backdropClick') {
        return;
      }
      onClose?.(evt, reason);
    };

    return (
      <MuiModal
        className={className}
        ref={ref}
        onClose={customOnClose}
        {...props}
      >
        <div>
          <Container
            title={title}
            onModalClose={(evt) => customOnClose(evt, 'escapeKeyDown')}
            hideCloseButton={hideCloseButton}
            children={children}
          />
        </div>
      </MuiModal>
    );
  }
);

/**
 * Customized MUI Modal component.
 *
 * It inherits all properties of a MUI Modal component.
 *
 * - modal can only be closed via esc key or dismiss button
 */
export const Modal = muiStyled(ModalImpl)(({ theme }) => css``);

export default Modal;
